import React, { useState, useEffect, useRef } from 'react';
import {
    FaMapMarkerAlt, FaUser, FaPhoneAlt, FaFolderOpen,
    FaChevronRight, FaRegFilePdf, FaLocationArrow, FaComments, FaHistory, FaPaperPlane,
    FaCamera, FaMicrophone, FaRoute, FaCheckCircle, FaExclamationTriangle, FaTrash,
    FaShoppingCart, FaImage
} from 'react-icons/fa';
import styles from '../../pages/Account.module.css';
import pb from '../../lib/pocketbase';
import { toast } from 'react-toastify';

const TabWorkSites = ({ staffProjects, loadingStaff, fetchStaffData, user }) => {
    const [selectedProject, setSelectedProject] = useState(null);
    const [activeTab, setActiveTab] = useState('journal');
    const [journalEntries, setJournalEntries] = useState([]);
    const [chatMessages, setChatMessages] = useState([]);
    const [chantierPhotos, setChantierPhotos] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Voice to Text States
    const [isRecording, setIsRecording] = useState(false);
    const [recognition, setRecognition] = useState(null);
    const [recordingProjectId, setRecordingProjectId] = useState(null);

    // Photo Upload States
    const fileInputRef = useRef(null);
    const [uploadingForProject, setUploadingForProject] = useState(null);

    // Init Speech Recognition
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const rec = new SpeechRecognition();
            rec.lang = 'fr-FR';
            rec.continuous = false;
            rec.interimResults = false;

            rec.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                saveJournalEntry(recordingProjectId, `Rapport vocal : "${transcript}"`, 'rapport');
                toast.success("Rapport vocal enregistré !");
            };

            rec.onend = () => {
                setIsRecording(false);
                setRecordingProjectId(null);
            };

            setRecognition(rec);
        }
    }, [recordingProjectId]);

    // Fetch Details when a project is opened
    useEffect(() => {
        if (selectedProject) {
            setLoadingDetails(true);
            Promise.all([
                fetchJournal(selectedProject.id),
                fetchChat(selectedProject.id),
                fetchChantierPhotos(selectedProject.id)
            ]).then(() => setLoadingDetails(false));

            pb.collection('site_activities').subscribe('*', e => {
                if (e.record.chantier === selectedProject.id) {
                    fetchJournal(selectedProject.id);
                    if (e.record.type === 'chat_msg') fetchChat(selectedProject.id);
                }
            }).catch(err => { if (err.status !== 0) console.error(err); });

            pb.collection('chantiers_photos').subscribe('*', e => {
                if (e.record.chantier === selectedProject.id) {
                    fetchChantierPhotos(selectedProject.id);
                }
            }).catch(err => { if (err.status !== 0) console.error(err); });

            return () => {
                pb.collection('site_activities').unsubscribe('*').catch(() => { });
                pb.collection('chantiers_photos').unsubscribe('*').catch(() => { });
            };
        }
    }, [selectedProject]);

    const fetchJournal = async (chantierId) => {
        try {
            const res = await pb.collection('site_activities').getList(1, 50, {
                filter: `chantier="${chantierId}" && type != 'chat_msg'`,
                sort: '-created',
                expand: 'author'
            });
            setJournalEntries(res.items);
        } catch (err) { console.error(err); }
    };

    const fetchChat = async (chantierId) => {
        try {
            const res = await pb.collection('site_activities').getList(1, 50, {
                filter: `chantier="${chantierId}" && type="chat_msg"`,
                sort: 'created',
                expand: 'author'
            });
            setChatMessages(res.items);
        } catch (err) { console.error(err); }
    };

    const fetchChantierPhotos = async (chantierId) => {
        try {
            const res = await pb.collection('chantiers_photos').getList(1, 100, {
                filter: `chantier="${chantierId}"`,
                sort: '-created',
                expand: 'worker'
            });
            setChantierPhotos(res.items);
        } catch (err) { console.error(err); }
    };

    const saveJournalEntry = async (chantierId, content, type = 'info', file = null, isUrgent = false) => {
        try {
            const formData = new FormData();
            formData.append('chantier', chantierId);
            formData.append('author', user.id);
            formData.append('content', content);
            formData.append('type', type);
            if (isUrgent) formData.append('is_urgent', true);
            if (file) formData.append('attachment', file);

            await pb.collection('site_activities').create(formData);
            if (selectedProject?.id === chantierId) fetchJournal(chantierId);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de l'enregistrement");
        }
    };

    const handleSendChat = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;
        try {
            await pb.collection('site_activities').create({
                chantier: selectedProject.id,
                author: user.id,
                type: 'chat_msg',
                content: newMessage
            });
            setNewMessage('');
        } catch (err) { toast.error("Erreur envoi"); }
    };

    // Quick Actions
    const handleVoiceRecord = (projectId) => {
        if (!recognition) {
            toast.error("La reconnaissance vocale n'est pas supportée par votre navigateur.");
            return;
        }
        if (isRecording) {
            recognition.stop();
        } else {
            setRecordingProjectId(projectId);
            setIsRecording(true);
            recognition.start();
            toast.info("Micro activé... Parlez maintenant.");
        }
    };

    const handleQuickPhoto = (projectId) => {
        setUploadingForProject(projectId);
        fileInputRef.current.click();
    };

    const onFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file || !uploadingForProject) return;

        try {
            toast.info("Envoi de la photo...");
            await saveJournalEntry(uploadingForProject, "Nouvelle photo ajoutée", 'photo', file);

            // Also add to chantiers_photos for gallery
            const photoForm = new FormData();
            photoForm.append('chantier', uploadingForProject);
            photoForm.append('file', file);
            photoForm.append('worker', user.id);
            photoForm.append('caption', `Photo Terrain - ${new Date().toLocaleDateString()}`);
            photoForm.append('type', 'INFO');
            await pb.collection('chantiers_photos').create(photoForm);

            toast.success("Photo enregistrée !");

            // Manual refresh for current user
            if (selectedProject?.id === uploadingForProject) {
                fetchChantierPhotos(uploadingForProject);
                fetchJournal(uploadingForProject);
            }
        } catch (err) {
            toast.error("Erreur d'envoi");
        } finally {
            setUploadingForProject(null); e.target.value = '';
        }
    };

    const handleMaterialNeed = async (project) => {
        const material = window.prompt(`Besoin matériel pour ${project.name} ? (Ex: Sacs de ciment, vis, etc.)`);
        if (material && material.trim()) {
            await saveJournalEntry(project.id, `🛒 BESOIN MATÉRIEL : ${material}`, 'logistique');
            toast.success("Demande matériel envoyée au dépôt !");
            // If modal is open on this project, refresh journal
            if (selectedProject?.id === project.id) fetchJournal(project.id);
        }
    };

    const handleSOS = async (project) => {
        if (!window.confirm("⚠️ Voulez-vous vraiment envoyer une alerte SOS ? Le bureau sera notifié immédiatement.")) return;

        let geoInfo = "";
        if ("geolocation" in navigator) {
            try {
                const position = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
                });
                geoInfo = ` (Position : ${position.coords.latitude.toFixed(5)}, ${position.coords.longitude.toFixed(5)})`;
            } catch (err) {
                console.warn("Géolocalisation refusée ou indisponible", err);
            }
        }

        await saveJournalEntry(project.id, `🚨 ALERTE SOS BLOQUANTE${geoInfo}. Intervention urgente nécessaire !`, 'alerte', null, true);
        toast.error("🚨 ALERTE ENVOYÉE ! Le bureau est prévenu.");
        // If modal is open on this project, refresh journal
        if (selectedProject?.id === project.id) fetchJournal(project.id);
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.siteHeader}>
                <div>
                    <h2 className={styles.siteTitle}>📦 Mes Dossiers Chantiers</h2>
                    <span className={styles.cardSubtitle} style={{ margin: 0 }}>
                        {staffProjects?.length || 0} Missions actives
                    </span>
                </div>
                <button className={styles.submitButton} style={{ width: 'auto', padding: '10px 25px' }} onClick={fetchStaffData} disabled={loadingStaff}>Actualiser</button>
            </div>

            <div className={styles.siteGrid}>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={onFileChange} capture="environment" />

                {staffProjects?.map((project, i) => (
                    <div key={project.id || i} className={styles.siteCard}>
                        {/* HEADER PREMIUM */}
                        <div className={styles.siteCardHeader}>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: project.status === 'EN_COURS' ? '#10b981' : project.status === 'PLANNING' ? '#3b82f6' : project.status === 'PAUSE' ? '#f59e0b' : '#64748b' }}></span>
                                    <h3 className={styles.siteTitle}>{project.name}</h3>
                                </div>
                                <div className={styles.siteAddress}>
                                    <FaMapMarkerAlt style={{ color: 'var(--color-primary)' }} /> {project.address || 'Adresse non spécifiée'}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    title="Itinéraire Maps"
                                    className={styles.accountHeaderBtn}
                                    style={{ background: 'var(--color-surface)', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                                    onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(project.address)}`, '_blank')}
                                >
                                    <FaRoute />
                                </button>
                            </div>
                        </div>

                        {/* QUICK ACTIONS GRID 2x2 */}
                        <div className={styles.siteActionGrid}>
                            <button
                                onClick={() => handleQuickPhoto(project.id)}
                                className={styles.siteActionBtn}>
                                <FaCamera color="#ec4899" /> Photos
                            </button>
                            <button
                                onClick={() => handleVoiceRecord(project.id)}
                                className={`${styles.siteActionBtn} ${isRecording && recordingProjectId === project.id ? styles.siteActionBtnRecording : ''}`}>
                                <FaMicrophone color={isRecording && recordingProjectId === project.id ? '#ef4444' : '#6366f1'} /> Vocal
                            </button>
                            <button
                                onClick={() => handleMaterialNeed(project)}
                                className={styles.siteActionBtn}>
                                <FaShoppingCart color="#f59e0b" /> Matériel
                            </button>
                            <button
                                onClick={() => handleSOS(project)}
                                className={styles.siteActionBtn} style={{ color: '#dc2626' }}>
                                <FaExclamationTriangle /> SOS
                            </button>
                        </div>

                        {/* INFO & FOLDER LINK */}
                        <div className={styles.siteFooter}>
                            <div className={styles.siteBadgeGrid}>
                                <div className={`${styles.siteBadge} ${styles.siteBadgeInfo}`}>
                                    <FaUser /> Client: {project.expand?.client?.name || 'Inconnu'}
                                </div>
                                {project.status === 'EN_COURS' && <div className={`${styles.siteBadge} ${styles.siteBadgeSuccess}`}><FaCheckCircle /> Actif</div>}
                                {project.status === 'PLANNING' && <div className={`${styles.siteBadge}`} style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}><FaCheckCircle /> Planifié</div>}
                                {project.status === 'PAUSE' && <div className={`${styles.siteBadge}`} style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}><FaExclamationTriangle /> En pause</div>}
                                {project.status === 'TERMINE' && <div className={`${styles.siteBadge}`} style={{ background: 'rgba(100, 116, 139, 0.1)', color: '#64748b' }}><FaCheckCircle /> Terminé</div>}
                                {!['EN_COURS', 'PLANNING', 'PAUSE', 'TERMINE'].includes(project.status) && <div className={`${styles.siteBadge} ${styles.siteBadgeDanger}`}><FaExclamationTriangle /> Suspendu</div>}
                            </div>

                            <button
                                className={styles.siteFullFolderBtn}
                                onClick={() => {
                                    setSelectedProject(project);
                                    setActiveTab('journal');
                                }}
                            >
                                <FaFolderOpen /> Ouvrir le Dossier Complet
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* PROJECT DETAIL MODAL (Dossier Complet) */}
            {selectedProject && (
                <div className={styles.modalOverlay} onClick={() => setSelectedProject(null)}>
                    <div className={`${styles.modalCard} ${styles.modalLarge}`} onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', borderRadius: '24px', overflow: 'hidden' }}>
                        <div className={styles.modalHeader} style={{ background: 'var(--color-secondary)', color: 'white', padding: '25px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{ background: 'rgba(255,255,255,0.2)', width: '45px', height: '45px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaFolderOpen />
                                </div>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <h3 style={{ margin: 0, color: 'white', fontSize: '1.4rem', wordBreak: 'break-word' }}>{selectedProject.name}</h3>
                                    <p style={{ margin: 0, opacity: 0.8, fontSize: '0.85rem' }}>Dossier technique complet</p>
                                </div>
                            </div>
                            <button className={styles.closeBtn} onClick={() => setSelectedProject(null)} style={{ color: 'white', background: 'rgba(255,255,255,0.1)' }}>×</button>
                        </div>

                        <div className={styles.tabsHeader}>
                            <button
                                className={`${styles.tabBtn} ${activeTab === 'journal' ? styles.tabBtnActive : ''}`}
                                onClick={() => setActiveTab('journal')}
                            >
                                <FaHistory /> Journal
                            </button>
                            <button
                                className={`${styles.tabBtn} ${activeTab === 'photos' ? styles.tabBtnActive : ''}`}
                                onClick={() => setActiveTab('photos')}
                            >
                                <FaImage /> Photos
                            </button>
                            <button
                                className={`${styles.tabBtn} ${activeTab === 'chat' ? styles.tabBtnActive : ''}`}
                                onClick={() => setActiveTab('chat')}
                            >
                                <FaComments /> Équipe
                            </button>
                            <button
                                className={`${styles.tabBtn} ${activeTab === 'docs' ? styles.tabBtnActive : ''}`}
                                onClick={() => setActiveTab('docs')}
                            >
                                <FaRegFilePdf /> Plans / Devis
                            </button>
                        </div>

                        <div className={styles.modalInner}>
                            {activeTab === 'journal' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {journalEntries.map(entry => (
                                        <div key={entry.id} className={styles.modalListItem} style={{ borderLeft: `5px solid ${entry.type === 'photo' ? '#ec4899' : entry.type === 'rapport' ? '#6366f1' : 'var(--color-primary)'}` }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'var(--color-primary-light, #f1f5f9)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '0.7rem', color: 'var(--color-primary)' }}>
                                                        {entry.expand?.author?.name?.[0] || 'U'}
                                                    </div>
                                                    <span style={{ fontWeight: '700', fontSize: '0.9rem', color: 'var(--color-text)' }}>{entry.expand?.author?.name || 'Inconnu'}</span>
                                                </div>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{new Date(entry.created).toLocaleString()}</span>
                                            </div>
                                            <div style={{ fontSize: '0.95rem', color: 'var(--color-text)', lineHeight: '1.5', opacity: 0.9 }}>{entry.content}</div>
                                            {entry.attachment && (
                                                <div style={{ marginTop: '15px', position: 'relative', borderRadius: '12px', overflow: 'hidden' }}>
                                                    <img src={pb.files.getURL(entry, entry.attachment)} alt="Preuve" style={{ width: '100%', maxHeight: '400px', objectFit: 'cover' }} />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {journalEntries.length === 0 && (
                                        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>
                                            <FaHistory size={40} style={{ opacity: 0.2, marginBottom: '15px' }} />
                                            <p>Aucune activité enregistrée sur ce chantier.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'chat' && (
                                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '400px' }}>
                                    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '20px' }}>
                                        {chatMessages.map(msg => {
                                            const isMe = msg.author === user.id;
                                            return (
                                                <div key={msg.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                                                    <div className={isMe ? '' : styles.chatBubble} style={{
                                                        background: isMe ? 'var(--color-primary)' : undefined,
                                                        color: isMe ? 'white' : undefined,
                                                        padding: '12px 18px',
                                                        borderRadius: '18px',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                                        borderBottomRightRadius: isMe ? '4px' : '18px',
                                                        borderBottomLeftRadius: isMe ? '18px' : '4px'
                                                    }}>
                                                        {!isMe && <div style={{ fontSize: '0.7rem', fontWeight: '800', marginBottom: '4px', opacity: 0.7 }}>{msg.expand?.author?.name}</div>}
                                                        {msg.content}
                                                    </div>
                                                    <div style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)', marginTop: '6px', textAlign: isMe ? 'right' : 'left', fontWeight: '600' }}>
                                                        {new Date(msg.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <form onSubmit={handleSendChat} style={{ display: 'flex', gap: '10px', background: 'var(--color-surface)', padding: '10px', borderRadius: '15px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid var(--color-border, rgba(0,0,0,0.05))' }}>
                                        <input
                                            type="text"
                                            className={styles.modalSelect}
                                            placeholder="Message vocal ou texte..."
                                            value={newMessage}
                                            onChange={e => setNewMessage(e.target.value)}
                                            style={{ flex: 1, border: 'none', background: 'transparent' }}
                                        />
                                        <button type="submit" className={styles.submitButton} style={{ width: '45px', height: '45px', borderRadius: '12px', padding: 0 }}>
                                            <FaPaperPlane />
                                        </button>
                                    </form>
                                </div>
                            )}

                            {activeTab === 'docs' && (
                                <div style={{ display: 'grid', gap: '15px' }}>
                                    <div className={styles.modalListItem} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <div style={{ width: '40px', height: '40px', background: '#fee2e2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                                                <FaRegFilePdf />
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '700', color: 'var(--color-text)' }}>Plans de masse.pdf</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Ajouté par le bureau le 12/02</div>
                                            </div>
                                        </div>
                                        <button className={styles.exportBtn} style={{ padding: '8px 20px', borderRadius: '10px' }}>Consulter</button>
                                    </div>
                                    <div className={styles.modalListItem} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <div style={{ width: '40px', height: '40px', background: '#fee2e2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                                                <FaRegFilePdf />
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '700', color: 'var(--color-text)' }}>Devis Signé.pdf</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Certifié par le client</div>
                                            </div>
                                        </div>
                                        <button className={styles.exportBtn} style={{ padding: '8px 20px', borderRadius: '10px' }}>Consulter</button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'photos' && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', gap: '20px' }}>
                                    {chantierPhotos.map(record => {
                                        // Handle the case where 'file' is a multi-file field (maxSelect: 99 in schema)
                                        const fileList = Array.isArray(record.file) ? record.file : [record.file];

                                        return fileList.map((fileName, idx) => (
                                            <div
                                                key={`${record.id}-${idx}`}
                                                style={{ position: 'relative', borderRadius: '15px', overflow: 'hidden', aspectRatio: '1/1', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', cursor: 'zoom-in' }}
                                                onClick={() => window.open(pb.files.getURL(record, fileName), '_blank')}
                                            >
                                                <img
                                                    src={pb.files.getURL(record, fileName, { thumb: '400x400' })}
                                                    alt={record.caption}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease' }}
                                                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                                                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                                />
                                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '10px', background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', color: 'white', fontSize: '0.75rem' }}>
                                                    <div style={{ fontWeight: '700' }}>{record.caption || 'Sans titre'}</div>
                                                    <div style={{ opacity: 0.8 }}>{new Date(record.created).toLocaleDateString()}</div>
                                                </div>
                                            </div>
                                        ));
                                    })}
                                    {chantierPhotos.length === 0 && (
                                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
                                            <FaImage size={50} style={{ opacity: 0.2, marginBottom: '20px' }} />
                                            <p>Aucune photo dans la galerie de ce chantier.</p>
                                            <button
                                                className={styles.submitButton}
                                                style={{ width: 'auto', marginTop: '15px', background: 'var(--color-primary)', color: 'white' }}
                                                onClick={() => handleQuickPhoto(selectedProject.id)}
                                            >
                                                Ajouter la première photo
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.05); opacity: 0.8; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </div>
    );
};

export default TabWorkSites;
